import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { SITE } from "@/constants/testIds";
import { SECTIONS } from "@/lib/sections";
import { CompanySearch } from "@/components/site/CompanySearch";

/**
 * Search-first sticky navigation, inspired by screener.in.
 * The search input takes the visual center of the header; section links are
 * secondary and compact. Optionally hide the search when the hero already has
 * one (homepage) by passing `hideSearch`.
 */
export function SiteNav({ hideSearch = false }) {
  const [open, setOpen] = useState(false);

  return (
    <header
      data-testid={SITE.nav}
      className="sticky top-0 z-40 border-b border-kira-border bg-kira-bg/95 backdrop-blur-md"
    >
      <div className="max-w-7xl mx-auto flex items-center gap-4 px-4 sm:px-6 h-14">
        <Link data-testid={SITE.navLogo} to="/" className="flex items-center gap-2.5 shrink-0">
          <div className="flex h-8 w-8 items-center justify-center rounded bg-kira-gold text-black font-chivo font-bold text-sm">K</div>
          <div className="leading-none hidden sm:block">
            <p className="font-chivo font-bold text-white text-sm">KIRA Score</p>
            <p className="text-[9px] text-kira-textMuted font-mono tracking-wide mt-0.5">RESEARCH BEFORE RETURNS</p>
          </div>
        </Link>

        {!hideSearch && (
          <div className="flex-1 max-w-md">
            <CompanySearch variant="nav" />
          </div>
        )}

        <nav className="hidden lg:flex items-center gap-0.5 ml-auto">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.key}
              data-testid={`${SITE.navSectionLink}-${s.key}`}
              to={`/research/${s.key}`}
              className={({ isActive }) =>
                `px-2.5 py-1.5 rounded text-xs transition-colors font-medium ${
                  isActive ? "text-kira-gold bg-kira-gold/10" : "text-kira-textMuted hover:text-white hover:bg-white/5"
                }`
              }
            >
              {s.short}
            </NavLink>
          ))}
        </nav>

        <button
          onClick={() => setOpen(!open)}
          className="lg:hidden ml-auto text-kira-textMuted hover:text-white p-2"
          data-testid="site-nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <nav className="lg:hidden border-t border-kira-border px-4 py-3 space-y-1 bg-kira-bg">
          {SECTIONS.map((s) => (
            <NavLink
              key={s.key}
              to={`/research/${s.key}`}
              onClick={() => setOpen(false)}
              className="block px-3 py-2 rounded-md text-sm text-kira-textMuted hover:text-white hover:bg-white/5"
            >
              {s.label}
            </NavLink>
          ))}
        </nav>
      )}
    </header>
  );
}
